import type { Editor } from '@tiptap/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { CorreoEventoDTO, CorreoModeloDTO, CreateCorreoModelRequest, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import { EMAIL_TEMPLATES_PATH, emailTemplatePath, errorMessage } from '../common/email-template-common'
import type { FormMessage } from '../common/email-template-common'
import { EmailTemplateMention, MENTION_NODE, normalizeMentionHtml } from '../common/email-template-mention'

export interface EmailTemplateFormState {
    id: number | null
    nombre: string
    evento: number | ''
    cuentaCC: string
    cuentaCCB: string
    asunto: string
    cuerpo: string
}

const LOAD_ERROR = 'No se pudo cargar el modelo de correo.'
const SAVE_ERROR = 'Error al guardar el modelo de correo'
const DELETE_ERROR = 'No se pudo eliminar el modelo de correo.'
const EMPTY_BODY_ERROR = 'Escribe el cuerpo del correo.'

export const EDITOR_EXTENSIONS = [EmailTemplateMention]

const emptyState: EmailTemplateFormState = {
    id: null,
    nombre: '',
    evento: '',
    cuentaCC: '',
    cuentaCCB: '',
    asunto: '',
    cuerpo: '',
}

const toFormState = (body: CorreoModeloDTO): EmailTemplateFormState => ({
    id: body.id,
    nombre: body.nombre || '',
    evento: body.evento?.id ?? '',
    cuentaCC: body.cuentaCC || '',
    cuentaCCB: body.cuentaCCB || '',
    asunto: body.asunto || '',
    cuerpo: body.cuerpo || '',
})

const toRequest = (state: EmailTemplateFormState, correoEventoId: number): CreateCorreoModelRequest => ({
    id: state.id,
    nombre: state.nombre,
    correoEventoId,
    cuentaCC: state.cuentaCC,
    cuentaCCB: state.cuentaCCB,
    asunto: state.asunto,
    cuerpo: normalizeMentionHtml(state.cuerpo),
})

const htmlToPlainText = (html: string) => html
    .replace(/<\/(p|li)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()

const isEmptyBody = (html: string) => htmlToPlainText(html).length === 0

export const useEmailTemplateFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { modeloId } = useParams()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || EMAIL_TEMPLATES_PATH
    const editorRef = useRef<Editor | null>(null)

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [eventos, setEventos] = useState<CorreoEventoDTO[]>([])
    const [state, setState] = useState<EmailTemplateFormState>(emptyState)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const eventosResponse: WrapperResponse<CorreoEventoDTO[]> | null = await (await APIInvoke.invokeGET('/correo-evento')).json()
                if (!active) return
                if (!eventosResponse?.ok) throw new Error(eventosResponse?.message || LOAD_ERROR)
                setEventos(eventosResponse.body || [])
                if (!modeloId) {
                    setState(emptyState)
                    return
                }
                const response: WrapperResponse<CorreoModeloDTO> | null = await (await APIInvoke.invokeGET(`/correo-modelo/${modeloId}`)).json()
                if (!active) return
                if (!response?.ok || !response.body) throw new Error(response?.message || LOAD_ERROR)
                setState(toFormState(response.body))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [modeloId])

    const evento = useMemo(() => eventos.find((item) => item.id === state.evento) || null, [eventos, state.evento])
    const campos = evento?.correoCampos || []

    const patch = (partial: Partial<EmailTemplateFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        nombre: (value: string) => patch({ nombre: clip(value, 80) }),
        evento: (value: number | '') => patch({ evento: value }),
        cuentaCC: (value: string) => patch({ cuentaCC: clip(value.trim(), 150) }),
        cuentaCCB: (value: string) => patch({ cuentaCCB: clip(value.trim(), 150) }),
        asunto: (value: string) => patch({ asunto: clip(value, 150) }),
        cuerpo: (value: string) => patch({ cuerpo: value }),
    }

    const insertField = (nombre: string) => {
        const editor = editorRef.current
        if (!editor) return
        editor.chain().focus().insertContent([{ type: MENTION_NODE, attrs: { value: nombre } }, { type: 'text', text: ' ' }]).run()
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!evento) return
        if (isEmptyBody(state.cuerpo)) {
            setMessage({ type: 'error', message: EMPTY_BODY_ERROR })
            return
        }
        try {
            setSaving(true)
            setMessage(null)
            const request = toRequest(state, evento.id)
            const response: WrapperResponse<CorreoModeloDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/correo-modelo/${state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/correo-modelo', request)).json()
            if (response.ok && response.body) {
                setMessage({ type: 'success', message: response.message || 'Modelo guardado correctamente' })
                if (!state.id) history.replace({ pathname: emailTemplatePath(response.body.id), state: { backUrl } })
                else setState(toFormState(response.body))
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar el modelo', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    const deleteTemplate = async () => {
        if (!state.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/correo-modelo/${state.id}`)).json()
            if (response.ok) history.push(backUrl)
            else setMessage({ type: 'error', message: response.message || DELETE_ERROR })
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
        }
    }

    return {
        form: state,
        eventos,
        evento,
        campos,
        loading,
        saving,
        deleting,
        busy: loading || saving || deleting,
        busyLabel: deleting ? 'Eliminando modelo…' : loading ? 'Cargando modelo…' : 'Guardando modelo…',
        message,
        title: state.id ? 'Editar modelo de correo' : 'Nuevo modelo de correo',
        isEdit: Boolean(state.id),
        backTo: backUrl,
        confirmDelete,
        change,
        insertField,
        setEditor: (editor: Editor | null) => { editorRef.current = editor },
        save,
        cancel: () => history.push(backUrl),
        deleteTemplate,
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
    }
}
